import { get, map, sortBy, toLower } from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Paper, TextField } from '@mui/material';
import { Edit, RemoveCircle } from '@mui/icons-material';
import { Tooltip } from '@fashionnovateam/nova-ui-toolkit';
import { makeStyles } from 'tss-react/mui';
import cn from 'classnames';
import BookingTypes from '../../Lib/Constants/booking-types';
import styles from './Deliveries.module.css';
import DeliveryCheckIn from './DeliveryCheckIn';
import BoxesReceivedInput from '../BoxesReceivedInput/BoxesReceivedInput';
import QuickOrderSearchModal from './QuickOrderSearchModal';
import { formatTime } from '../../Lib/Utils/Date/format-booking-timestamp.ts';

const useStyles = makeStyles()(() => ({
  notes: {
    fontSize: '12px',
  },
}));

const includesText = (value, text) =>
  toLower(value || '').includes(toLower(text || ''));

const isLate = (booking) => {
  if (!booking.delivered_at) return false;

  const differenceInHours = Math.abs(
    moment
      .utc(booking.delivered_at)
      .diff(moment.utc(booking.starts_at), 'hours'),
  );

  return differenceInHours >= 24
    ? differenceInHours - 24 >= 1
    : differenceInHours >= 1;
};

export default function BookingRow(props) {
  const { classes, cx } = useStyles();
  const {
    booking,
    editBooking,
    removeBooking,
    saveDelivery,
    currentUser,
    rowPerms,
    pagePerms,
    checkInDelivery,
  } = props;
  const isBlock = booking.type === BookingTypes.BLOCK;
  const [notes, setNotes] = useState(booking.notes || '');
  const [searchPO, setSearchPO] = useState(null);
  const purchaseOrderIds = map(booking.purchase_orders, 'po_number');

  function calculateBoxesRemaining() {
    const boxes = booking.boxes ? booking.boxes : 0;
    const received = booking.boxes_received ? +booking.boxes_received : 0;
    const result = received - boxes;
    return `${result > 0 ? '+' : ''}${Math.abs(result)}`;
  }

  const tooltipTitleFromColor = {
    priority: 'PO marked as "Priority"',
    cold: 'PO marked as "Cold"',
    slow: 'Memo contains "slow"',
    airDelivery: 'Shipment mode is "Air"',
  };

  const getColorByPO = useCallback(
    (po) => {
      if (includesText(po.memo, 'priority')) return 'priority';
      if (includesText(po.memo, 'cold')) return 'cold';
      if (includesText(po.shipment_mode, 'air')) return 'airDelivery';
      if (includesText(po.memo, 'slow')) return 'slow';
      return '';
    },
    [booking.purchase_orders],
  );

  return (
    <Paper
      className={cx([styles.bookingRow], {
        [styles.blockRow]: isBlock,
        [styles.lateDelivery]: isLate(booking),
      })}
      data-testid="deliveries-booking-row"
    >
      <div className={styles.bookingCell}>{formatTime(booking)}</div>

      <div className={styles.bookingCell}>{get(booking, 'reference')}</div>

      <div className={cx(styles.bookingCell, styles.paddingRight)}>
        {get(booking, 'vendor.company_name')}
      </div>

      <div className={styles.bookingCell}>{get(booking, 'vendor.grade')}</div>

      <div className={cn(styles.bookingCell, styles.bookingCellList)}>
        {map(sortBy(booking.purchase_orders, 'po_number'), (po) => (
          <Tooltip
            key={po.id}
            position="top"
            content={tooltipTitleFromColor[getColorByPO(po)]}
            className={cx({
              invisible: getColorByPO(po) === '',
            })}
          >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
            <div
              onClick={() => setSearchPO(po.po_number)}
              className={cx({
                [styles.poLink]: true,
                [styles.slow]: getColorByPO(po) === 'slow',
                [styles.priority]: getColorByPO(po) === 'priority',
                [styles.cold]: getColorByPO(po) === 'cold',
                [styles.airDelivery]: getColorByPO(po) === 'airDelivery',
              })}
              data-highlight={getColorByPO(po) || undefined}
            >
              {po.po_number}
            </div>
          </Tooltip>
        ))}
      </div>

      <div className={styles.bookingCell}>
        {map(sortBy(booking.purchase_orders, 'po_number'), (po) => (
          <div key={po.id}>{po.new_item ? 'NEW' : 'R/S'}</div>
        ))}
      </div>

      <div className={styles.bookingCell}>
        {booking.boxes ? numeral(booking.boxes).format('0,0') : ''}
      </div>

      <div className={cx(styles.bookingCell)}>
        {isBlock ? (
          ''
        ) : (
          <BoxesReceivedInput
            delivery={booking}
            onUpdate={saveDelivery}
            disabled={!rowPerms.canUpdateDelivery}
          />
        )}
      </div>

      <div className={cx(styles.bookingCell)}>
        {isBlock ? '' : `${calculateBoxesRemaining()}`}
      </div>

      <div className={styles.bookingCell}>
        {booking.units ? numeral(booking.units).format('0,0') : ''}
      </div>

      <div className={cx(styles.bookingCell, styles.bookingCellCheckIn)}>
        {isBlock ? (
          ''
        ) : (
          <DeliveryCheckIn
            delivery={booking}
            onSaveDelivery={saveDelivery}
            onCheckIn={checkInDelivery}
            disabled={!rowPerms.canUpdateDelivery}
          />
        )}
      </div>

      <div className={cx(styles.bookingCell, styles.bookingCellNotes)}>
        <TextField
          InputProps={{ classes: { input: classes.notes } }}
          value={notes}
          disabled={!rowPerms.canUpdateDelivery}
          onChange={(e) => {
            setNotes(e.target.value);
          }}
          onBlur={(e) => {
            const val = e.target.value;
            if (booking.notes !== val) {
              saveDelivery(
                booking.id,
                { id: booking.id, notes: val },
                purchaseOrderIds,
              );
            }
          }}
          placeholder="Notes..."
          multiline
          variant="outlined"
          margin="dense"
        />
      </div>

      {(rowPerms.canUpdateDelivery || rowPerms.canDeleteDelivery) && (
        <div className={styles.bookingCell}>
          {rowPerms.canUpdateDelivery && (
            <Edit
              onClick={() => rowPerms.canUpdateDelivery && editBooking(booking)}
              className={styles.bookingAction}
            />
          )}
          {rowPerms.canDeleteDelivery && (
            <RemoveCircle
              onClick={() =>
                rowPerms.canDeleteDelivery && removeBooking(booking)
              }
              className={styles.bookingAction}
            />
          )}
        </div>
      )}
      {searchPO && (
        <QuickOrderSearchModal
          open
          currentUser={currentUser}
          defaultValue={searchPO}
          onClose={() => setSearchPO(null)}
          perms={pagePerms}
        />
      )}
    </Paper>
  );
}

BookingRow.propTypes = {
  booking: PropTypes.shape({}).isRequired,
  currentUser: PropTypes.shape({}).isRequired,
  editBooking: PropTypes.func.isRequired,
  removeBooking: PropTypes.func.isRequired,
  saveDelivery: PropTypes.func.isRequired,
  checkInDelivery: PropTypes.func.isRequired,
  updateRole: PropTypes.bool,
  rowPerms: PropTypes.shape({
    canUpdateDelivery: PropTypes.bool,
    canDeleteDelivery: PropTypes.bool,
  }).isRequired,
  pagePerms: PropTypes.shape({
    canUpdatePackingList: PropTypes.bool,
    canReadPackingList: PropTypes.bool,
  }).isRequired,
};
